/**
 * 구간 식별색.
 *
 * 담당자가 6년째 쓰는 `docs/refs/client-legacy-docs/sample/26년 6월 배차현황.xlsx` 에서
 * 그대로 뽑아 왔다. 그 시트는 구간 칸마다 배경색을 칠해 두는데, 배차 등록 화면이
 * 자사 11대 × 3줄이라 한 눈에 서른 줄 넘는 구간을 가려내야 하기 때문이다
 * (고객 요청 2026-09-16 — *"구간이 많아 한 눈에 알아보기 위함"*).
 *
 * **이 색은 장식이 아니라 데이터 차원이다.** CLAUDE.md 의 무채색 원칙은 그대로 두고,
 * 색이 닿는 자리는 구간 값 하나(`RouteChip`)로 묶는다. 버튼·행 배경·상태 꼬리표에는
 * 여전히 색을 쓰지 않는다 — 거기까지 번지면 색이 의미를 잃는다.
 */

/** 레거시 시트 실측 색. 밝은 것부터 어두운 것 순으로 늘어놓아 팔레트가 읽히게 한다 */
export const ROUTE_COLORS: readonly string[] = [
  "#FBE4D5",
  "#FFD965",
  "#FFFF00",
  "#7EECC2",
  "#00B050",
  "#33CCFF",
  "#8EAADB",
  "#B4C6E7",
  "#ADB9CA",
  "#F4B083",
  "#FF0000",
  "#7030A0",
  "#2F5496",
  "#7F6000",
  "#0C0C0C",
];

/**
 * 배경색 위에 올릴 글자색.
 *
 * 팔레트에 `#0C0C0C`·`#7030A0` 같은 어두운 색이 섞여 있어 글자를 검정으로 고정하면
 * 그 구간만 읽히지 않는다. 상대휘도(WCAG)로 갈라 어두우면 흰 글자로 뒤집는다.
 */
export function readableTextColor(hex: string): string {
  const v = normalizeHex(hex);
  if (!v) return "#111827";
  const channel = (i: number) => {
    const c = parseInt(v.slice(1 + i * 2, 3 + i * 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
  return luminance > 0.45 ? "#111827" : "#FFFFFF";
}

/** `#RRGGBB` 만 통과시킨다. 서버가 같은 형식으로 검증하므로 화면도 같은 기준을 쓴다 */
export function normalizeHex(value: string | null | undefined): string | null {
  if (!value) return null;
  const v = value.trim().toUpperCase();
  return /^#[0-9A-F]{6}$/.test(v) ? v : null;
}


// ---------------------------------------------------------------------------
// HSL ↔ HEX — 커스텀 색 고르기 (담당자 요청 2026-09-17)
// ---------------------------------------------------------------------------
//
// > *"대표 색깔 외에도 커스텀 색깔 고를 수 있게 채도, 명도 조절해서 색깔 고를 수 있는
// >  파레트 그거 추가해줘"*
//
// 프리셋 15색은 레거시 시트에서 온 것이라 그 밖의 구간에는 쓸 색이 없다. 색상(H)은
// 가로 막대로, 채도·명도(S·L)는 2차원 판에서 고른다 — 색을 고르는 곳의 표준 모양이다.

export interface Hsl {
  h: number;   // 0–360
  s: number;   // 0–100
  l: number;   // 0–100
}

export function hslToHex({ h, s, l }: Hsl): string {
  const sat = s / 100;
  const lum = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sat * Math.min(lum, 1 - lum);
  const f = (n: number) =>
    Math.round(255 * (lum - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))));
  const hex = (v: number) => v.toString(16).padStart(2, "0").toUpperCase();
  return `#${hex(f(0))}${hex(f(8))}${hex(f(4))}`;
}

export function hexToHsl(hex: string): Hsl {
  const v = normalizeHex(hex) ?? "#4A86E8";
  const r = parseInt(v.slice(1, 3), 16) / 255;
  const g = parseInt(v.slice(3, 5), 16) / 255;
  const b = parseInt(v.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}
